import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from "axios";
import { Navigate, useNavigate } from 'react-router';
import { Loader2, LogOut, Bus, UtensilsCrossed, X, Check, Info, CreditCard } from 'lucide-react';
import { toast } from 'react-toastify';
import RatingButton from './RatingButton';
import { useFloorManager } from '../context/FloorManagerContext';

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useFloorManager();
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [dateError, setDateError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [updatingRecordId, setUpdatingRecordId] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRejectingId, setCurrentRejectingId] = useState(null);
  
  const observer = useRef();
  const loadingRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const handleLogout = () => {
  if (window.confirm('Are you sure you want to logout?')) {
    logout();
    toast.success('Logged out successfully');
    navigate('/lkart/supervisor-login');
  }
};

  const calculateWorkingHours = (entryTime, exitTime) => {
    if (!entryTime || !exitTime) return null;
    
    try {
      let entry, exit;
      
      if (entryTime.includes('T') && entryTime.endsWith('Z')) {
        entry = new Date(entryTime);
      } else {
        const [entryHours, entryMinutes] = entryTime.split(':');
        entry = new Date();
        entry.setHours(parseInt(entryHours), parseInt(entryMinutes || 0), 0, 0);
      }
      
      if (exitTime.includes('T') && exitTime.endsWith('Z')) {
        exit = new Date(exitTime);
      } else {
        const [exitHours, exitMinutes] = exitTime.split(':');
        exit = new Date();
        exit.setHours(parseInt(exitHours), parseInt(exitMinutes || 0), 0, 0);
      }
      
      let diff = exit - entry;
      if (diff < 0) {
        exit.setDate(exit.getDate() + 1);
        diff = exit - entry;
      }

      const hours = diff / (1000 * 60 * 60);
      const roundedHours = Math.ceil(hours * 100) / 100;

      return roundedHours.toFixed(2);
    } catch (e) {
      console.error('Error calculating working hours:', e);
      return null;
    }
  };

  const fetchAttendanceData = async (page = 1, isNewSearch = false) => {
    setLoading(true);
    try {
      const department = localStorage.getItem("department");
      const departmentId = localStorage.getItem("departmentId");
      const myCompany = user?.companyId;
if (!departmentId || !myCompany) {
  console.warn('fetchAttendanceData: departmentId or companyId not available yet, skipping.');
  setLoading(false);
  return;
}

      const encodedDepartment = encodeURIComponent(department);
      
      const response = await axios.get(
        `${BASE_URL}/rating-lenskart/all?department=${encodedDepartment}&departmentId=${departmentId}&companyId=${myCompany}`,
        {
          params: {
            page: page
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
        
      const { attendanceRecords, totalPages, currentPage } = response.data;
      
      const transformedData = attendanceRecords.map(worker => ({
        id: worker._id,
        userId: worker.userId?._id,
        name: worker.userId?.name || '',
        phone: worker.userId?.phone || '',
        profilePhotoUrl: worker.userId?.profilePhotoUrl || null,
        entryDateTime: worker.EntryDate,
        exitDateTime: worker.ExitDate,
        workingHours: worker.workingHours || 
                     calculateWorkingHours(worker.EntryDate, worker.ExitDate),
        rating: worker.rating || null,
        comments: worker.comment || [],
        busEntry: worker.busEntry || false,
        busExit: worker.busExit || false,
        haveFood: worker.haveFood || false,
        workerStatus: worker.WorkerAccepted || null,
        rejectionReason: worker.rejectionReason || null,
        paymentStatus: worker.paymentStatus || 'pending',
        status: worker.paymentStatus || 'pending'
      }));
      
      setTotalPages(totalPages);
      
      if (isNewSearch) {
        setAttendanceData(transformedData);
      } else {
        setAttendanceData(prev => [...prev, ...transformedData]);
      }
      
      setHasMore(currentPage < totalPages);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setHasMore(false);
      setErrorMessage('Failed to fetch attendance data. Please try again.');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };
  
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prevPage => prevPage + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const formatDateTime = (dateTimeString, showIcons = false, busEntry = false, busExit = false, haveFood = false) => {
    if (!dateTimeString) return '-';
  
    try {
      const [datePart, timePartFull] = dateTimeString.split('T');
      if (!timePartFull) return '-';
  
      const timePart = timePartFull.slice(0, 5);
      let [hours, minutes] = timePart.split(':').map(Number);
  
      const ampm = hours >= 12 ? 'pm' : 'am';
      const hour12 = hours % 12 || 12;
      const formattedTime = `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  
      const date = new Date(datePart);
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
  
      return (
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            {formattedDate}
            {showIcons && busEntry && (
              <Bus
                className="h-4 w-4 text-[#3D5A80]"
                title="Used bus service for entry"
              />
            )}
            {showIcons && busExit && (
              <Bus 
                className="h-4 w-4 text-[#3D5A80]" 
                title="Used bus service for exit" 
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
            {formattedTime}
            {showIcons && haveFood && (
              <UtensilsCrossed 
                className="h-3 w-3 text-[#F4A261]" 
                title="Availed food service" 
              />
            )}
          </div>
        </div>
      );
    } catch (e) {
      console.error('Error formatting date/time:', e);
      return '-';
    }
  };
  
  const getCommentValue = (comments, key) => {
    if (!comments || !Array.isArray(comments)) return '';
    
    const commentObj = comments.find(c => c[key] !== undefined);
    if (commentObj) return commentObj[key];
    
    const keyValueComment = comments.find(c => c.key === key);
    return keyValueComment ? keyValueComment.value : '';
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedPhoto('');
  };

  const handleRatingSubmit = async (reviewData, shiftId) => {
    setIsSubmittingRating(true);
    try {
      let comment = [];

      if (reviewData.stars <= 3) {
        if (reviewData.discipline) {
          comment.push({ "Discipline": reviewData.discipline });
        }
        if (reviewData.qualityOfWork) {
          comment.push({ "Quality of Work": reviewData.qualityOfWork });
        }
        if (reviewData.behaviour) {
          comment.push({ "Behaviour": reviewData.behaviour });
        }
      }

      const myCompany = user?.companyId;
      
      const requestData = {
        companyId: myCompany,
        shiftId: shiftId,
        stars: reviewData.stars,
        ...(comment.length > 0 && { comment })
      };

      const endpoint = `${BASE_URL}/rating-lenskart/add`;
      
      await axios.post(endpoint, requestData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      setAttendanceData(prev => 
        prev.map(record => 
          record.id === shiftId 
            ? {
                ...record,
                rating: reviewData.stars,
                comments: comment
              }
            : record
        )
      );
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2000);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      setErrorMessage('Failed to submit review. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleStatusUpdate = async (shiftId, status, reason = "") => {
    if (!shiftId) {
      return;
    }

    setUpdatingRecordId(shiftId);
    try {
      const token = localStorage.getItem("supervisor_token");
      
      const payload = {
        shiftId: shiftId,
        workerAccepted: status
      };

      if (status === 'rejected' && reason) {
        payload.reason = reason;
      }

      const response = await axios.post(
        `${BASE_URL}/floor-admin/select-worker`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setAttendanceData(prev => 
          prev.map(record => 
            record.id === shiftId 
              ? {
                  ...record,
                  workerStatus: status,
                  rejectionReason: status === 'rejected' ? reason : null
                }
              : record
          )
        );
        
        toast.success(`Worker ${status} successfully`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setErrorMessage('Failed to update worker status. Please try again.');
      setShowErrorModal(true);
    } finally {
      setUpdatingRecordId(null);
    }
  };

  const handleRejectClick = (shiftId) => {
    setCurrentRejectingId(shiftId);
    setRejectionReason("");
    setShowRejectionModal(true);
  };

  const handleRejectConfirm = () => {
    if (rejectionReason.trim().length < 3) {
      alert("Flag reason must be at least 3 characters long");
      return;
    }
    
    if (rejectionReason.trim().length > 100) {
      alert("Flag reason must not exceed 100 characters");
      return;
    }
    
    handleStatusUpdate(currentRejectingId, 'rejected', rejectionReason.trim());
    setShowRejectionModal(false);
    setRejectionReason("");
    setCurrentRejectingId(null);
  };

  const handleRejectCancel = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
    setCurrentRejectingId(null);
  };

  const fetchCompanyDetails = useCallback(async (companyId) => {
    try {
      setLoadingCompany(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.name) {
        setCompanyName(response.data.name);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
      setCompanyName('N/A');
    } finally {
      setLoadingCompany(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
  if (user?.companyId) {
    fetchCompanyDetails(user.companyId);
  }
}, [user?.companyId, fetchCompanyDetails]);

  const handleRowClick = (userId) => {
    navigate(`/lkart/supervisor/view-ratings/${userId}`);
  };

  const areActionsEnabled = (record) => {
    return record.paymentStatus === 'pending';
  };

  const handleCheckboxChange = (record) => {
    if (!areActionsEnabled(record) || updatingRecordId !== null) return;
    
    if (record.workerStatus === 'rejected') {
      handleStatusUpdate(record.id, 'accepted');
    } else {
      handleRejectClick(record.id);
    }
  };

  useEffect(() => {
  if (token && user?.companyId) {
    fetchAttendanceData(1, true);
  }
}, [token, user?.companyId]);

useEffect(() => {
  if (token && currentPage > 1 && hasMore) {
    fetchAttendanceData(currentPage);
  }
}, [currentPage]);

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0">
      <div className="max-w-7xl mx-auto bg-white shadow-lg rounded-lg p-2 relative">
        
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-center">Department Manager Dashboard</h2>
          <div className="flex justify-end mb-5">
            <button
              onClick={() => navigate("/lkart/supervisor/requirements")}
              className="flex items-center gap-1 px-3 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
              style={{ backgroundColor: '#4F46E5' }}
            >
              <span>Add/View Requirement</span>
            </button>
          </div>
          <div className="text-center mt-2">
            {loadingCompany ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-gray-600">Loading company...</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-700">
                Company: {companyName || 'N/A'} | Department: {localStorage.getItem("department") || 'N/A'}
              </p>
            )}
          </div>
        </div>
        
        {/*
          FIX (tooltip clipping): Remove overflow-y-auto from this wrapper and instead
          let the outer min-h-screen div handle scrolling. The table itself must NOT be
          inside an overflow:hidden/auto ancestor or the z-50 tooltips will be clipped
          behind the sticky header.
          We use a plain div with no overflow restriction here so tooltips can escape.
        */}
        <div className="w-full">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white z-20 shadow-md">
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Name <br /> (Phone No.)</th>
                <th className="p-2 text-center">Entry Date & Time</th>
                <th className="p-2 text-center">Exit Date & Time</th>
                <th className="p-2 text-center">Working Hour</th>
                <th className="p-2 text-center">Rating</th>
                {/* FIX: give Actions header a higher z-index so it always sits on top */}
                <th className="p-2 text-center w-48 relative z-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && currentPage === 1 ? (
                <tr>
                  <td colSpan="6" className="text-center p-4">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : attendanceData.length > 0 ? (
                attendanceData.map((record, index) => (
                  <tr 
                    key={index} 
                    ref={index === attendanceData.length - 1 ? lastElementRef : null}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="p-2 text-left min-w-[140px] cursor-pointer" onClick={() => handleRowClick(record.userId)}>
                      <div className="flex items-center">
                        {record.profilePhotoUrl && (
                          <img
                            src={record.profilePhotoUrl}
                            alt="Profile"
                            className="w-12 h-12 rounded-full object-fill cursor-pointer border border-gray-300 hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePhotoClick(record.profilePhotoUrl);
                            }}
                          />
                        )}
                        <div className="ml-2">
                          <div className="font-medium">{record.name}</div>
                          <div className="text-xs text-gray-500">{record.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      {formatDateTime(record.entryDateTime, true, record.busEntry, false, false)}
                    </td>
                    <td className="p-2 text-center">
                      {formatDateTime(record.exitDateTime, true, false, record.busExit, record.haveFood)}
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs">
                          {record.workingHours !== null ? `${record.workingHours} hrs` : '-'}
                        </span>
                      </div>
                    </td>
                   
                    <td className="p-2 text-center">
                      <RatingButton
                        record={record}
                        onRatingSubmit={handleRatingSubmit}
                        isSubmitting={isSubmittingRating}
                      />
                    </td>

                    <td className="p-2 text-center">
                      <div className="flex justify-center items-center gap-2">
                        {updatingRecordId === record.id ? (
                          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Checkbox for flagging/unflagging */}
                            {/*
                              FIX: tooltip now opens downward (top-full + mt-1) so it never
                              overlaps the sticky header when scrolling up.
                            */}
                            <div className="relative group">
                              <input
                                type="checkbox"
                                checked={record.workerStatus === 'rejected'}
                                onChange={() => handleCheckboxChange(record)}
                                disabled={!areActionsEnabled(record) || updatingRecordId !== null}
                                className="w-5 h-5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                              />
                              <span className="absolute top-full left-1/2 mt-1 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-30">
                                {areActionsEnabled(record) 
                                  ? (record.workerStatus === 'rejected' ? "Unflag" : "Flag") 
                                  : "Payment Processed"}
                              </span>
                            </div>

                            {/* Flagged status badge with reason tooltip */}
                            {/*
                              FIX: tooltip opens downward (top-full + mt-2) instead of upward,
                              preventing it from floating above the sticky header on scroll.
                            */}
                            {record.workerStatus === 'rejected' && record.rejectionReason && (
                              <div className="relative group">
                                <Info className="h-4 w-4 text-red-600 cursor-help" />
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-red-100 text-gray-900 text-xs rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-normal w-48 z-30">
                                  <div className="font-semibold mb-1">Flag Reason:</div>
                                  <div>{record.rejectionReason}</div>
                                  {/* Arrow now points UP (toward the icon) */}
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-0">
                                    <div className="border-4 border-transparent border-b-red-100"></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center p-4 text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {loading && currentPage > 1 && (
            <div className="flex justify-center items-center py-4" ref={loadingRef}>
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </div>

        {showPhotoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
            <div className="relative max-w-md">
              <button
                onClick={closePhotoModal}
                className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 z-10"
              >
                <X size={24} className="text-gray-600" />
              </button>
              <div className="relative">
                <img
                  src={selectedPhoto}
                  alt="Profile"
                  className={`w-full h-auto rounded-lg max-h-[80vh] object-fill`}
                />
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="text-green-500 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium">Rating submitted successfully!</h3>
            </div>
          </div>
        )}

        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="text-red-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-center mb-4">Error</h3>
              <p className="text-gray-600 text-center mb-6">{errorMessage}</p>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showRejectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4">Flag Reason</h3>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Please provide a reason for flagging <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => {
                    if (e.target.value.length <= 100) {
                      setRejectionReason(e.target.value);
                    }
                  }}
                  placeholder="Enter flag reason (min 3 characters, max 100 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows="4"
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-500">
                    {rejectionReason.length}/100
                  </span>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleRejectCancel}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={rejectionReason.trim().length < 3}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
                >
                  Flag
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 text-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 hover:underline transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupervisorDashboard;